import type { LocalMatchHost } from '../matchHost/LocalMatchHost.js';
import type { MatchLifecycleEvent, PlayerView, Unsubscribe } from '../matchHost/MatchHost.js';
import type { Command } from '../simulation/commands/Command.js';
import type { PlayerId } from '../simulation/ids.js';

import type { MatchConnection } from './MatchConnection.js';

// Defensive logger for shared code. The package builds against pure
// ES2022 with `lib: ["ES2022"]` and `types: []` so it stays runtime-
// agnostic (browser + Node + headless playtest runner), which means
// `console` is not in the type environment. Both runtimes provide it
// via globalThis at execution time, so we grab it through a guarded
// accessor and swallow the lookup if it's missing. This is the same
// pattern used by other shared-targeting libraries (e.g. zod) to log
// without coupling to DOM or Node typings.
function logHandlerError(label: string, err: unknown): void {
  const g = globalThis as { console?: { warn?: (...args: unknown[]) => void } };
  try {
    g.console?.warn?.(`[${label}] threw`, err);
  } catch {
    // Hostile globalThis (Object.freeze, console replaced with a
    // throwing proxy in some test harnesses) — non-fatal.
  }
}

// In-process connection: send/receive are direct method calls on the host. The same
// surface is implemented by ColyseusMatchConnection in V2; client code consumes only
// the MatchConnection interface.
export class LocalMatchConnection implements MatchConnection {
  private playerId: PlayerId | null = null;
  private snapshotHandlers = new Set<(s: PlayerView) => void>();
  private lifecycleHandlers = new Set<(e: MatchLifecycleEvent) => void>();
  private unsubSnapshot: Unsubscribe | null = null;
  private unsubLifecycle: Unsubscribe | null = null;

  constructor(private readonly host: LocalMatchHost) {}

  async connect(playerId: PlayerId): Promise<void> {
    this.playerId = playerId;
    this.host.registerObserver(playerId);
    this.unsubSnapshot = this.host.subscribe((snap, recipient) => {
      if (recipient !== playerId) return;
      // Error containment: any one handler throwing must NOT prevent
      // subsequent handlers from receiving the snapshot, AND must NOT
      // propagate back into the host's emit loop (which would kill the
      // match for every client). 13+ client-side subscribers (Renderer,
      // Sfx, Music, HUD, BuildPanel, Minimap, KillEvents, EndOfMatch,
      // EffectsLayer, FloatingTextLayer, RelayUnderAttackAlert,
      // DoctrinePicker, Tutorial) each receive every tick — one bad
      // shape, one Phaser-graphics-destroyed race, one audio context
      // throw would otherwise drop the whole match.
      for (const fn of this.snapshotHandlers) {
        try {
          fn(snap);
        } catch (err) {
          logHandlerError('snapshot handler', err);
        }
      }
    });
    this.unsubLifecycle = this.host.onLifecycle((event) => {
      // Same containment for lifecycle events. A throw in (say) the EOM
      // handler must not block ReconnectBanner from receiving the same
      // event, and a throw must not climb back to the host.
      for (const fn of this.lifecycleHandlers) {
        try {
          fn(event);
        } catch (err) {
          logHandlerError('lifecycle handler', err);
        }
      }
    });
  }

  async disconnect(): Promise<void> {
    this.unsubSnapshot?.();
    this.unsubLifecycle?.();
    this.unsubSnapshot = null;
    this.unsubLifecycle = null;
    this.playerId = null;
  }

  send(command: Command): void {
    if (this.playerId === null) return;
    this.host.submit(this.playerId, command);
  }

  onSnapshot(handler: (snapshot: PlayerView) => void): Unsubscribe {
    this.snapshotHandlers.add(handler);
    return () => {
      this.snapshotHandlers.delete(handler);
    };
  }

  onLifecycle(handler: (event: MatchLifecycleEvent) => void): Unsubscribe {
    this.lifecycleHandlers.add(handler);
    return () => {
      this.lifecycleHandlers.delete(handler);
    };
  }
}
